import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const IMG = "https://images.pexels.com/photos/8092312/pexels-photo-8092312.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const PHASES = [
  {
    n: "01",
    t: "Diagnose",
    body:
      "We map the opportunity against technical reality, capital structure, regulation and carbon. No engagement starts until the problem is defined the same way in the boardroom and on site.",
    outputs: ["Opportunity & risk map", "Tech / partner shortlist", "Capital structure brief"],
  },
  {
    n: "02",
    t: "Design",
    body:
      "We architect the solution end-to-end — commercial model, technology stack, permitting pathway, ESG / carbon approach, governance — and pressure-test it against execution.",
    outputs: ["Integrated business case", "Tech & offtake architecture", "ESG & carbon plan"],
  },
  {
    n: "03",
    t: "Build",
    body:
      "We stay through the hard part. Procurement, EPC governance, construction oversight, commissioning and ramp-up — with transparent weekly metrics and a single owner of delivery.",
    outputs: ["Owners-engineer function", "Delivery & commissioning", "Operations readiness"],
  },
  {
    n: "04",
    t: "Operate",
    body:
      "We don't disappear at hand-over. We partner on performance — yield, energy, emissions, OPEX — through contracts, capital or operating partnerships, depending on what the asset needs.",
    outputs: ["Performance contracts", "Continuous improvement", "Long-term operating partnership"],
  },
];

export default function Approach() {
  return (
    <div data-testid="approach-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-20">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-6">
          Approach
        </p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter font-medium max-w-5xl">
          A method that ends with an <em className="not-italic text-[#0F7A3E]">operating</em> asset.
        </h1>
        <p className="mt-10 max-w-2xl text-lg text-[#575B57] leading-relaxed">
          Our four-phase operating method is how we guarantee that advisory,
          capital and execution pull in the same direction — and that the
          outcomes you signed up for are the outcomes you measure.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="sticky top-24">
              <div className="relative overflow-hidden aspect-[4/5] w-full">
                <img src={IMG} alt="A clean, orderly workspace symbolising methodical execution" className="absolute inset-0 w-full h-full object-cover saafi-img-reveal" />
              </div>
              <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-[#79836B]">
                — The operating method, captured
              </p>
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="border-t border-[#1A1C1A]">
              {PHASES.map((p) => (
                <div
                  key={p.n}
                  data-testid={`approach-phase-${p.n}`}
                  className="group border-b border-[#E4E5DF] py-10 md:py-14"
                >
                  <div className="flex items-baseline gap-6">
                    <span className="font-mono-alt text-xs text-[#79836B]">
                      / {p.n}
                    </span>
                    <h3 className="font-display text-3xl md:text-5xl leading-tight tracking-tight font-medium text-[#1A1C1A]">
                      {p.t}
                    </h3>
                  </div>
                  <p className="mt-6 text-lg leading-relaxed text-[#575B57] max-w-2xl">
                    {p.body}
                  </p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    {p.outputs.map((o) => (
                      <span key={o} className="inline-block bg-[#F3F3E8] text-xs tracking-wide px-4 py-2 text-[#1A1C1A] border border-[#E4E5DF]">
                        {o}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#121312] text-[#FBFBF9] saafi-grain relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight font-medium max-w-3xl">
            Our method only works if you want an asset, not an artifact.
          </h2>
        </div>
      </section>
    </div>
  );
}
